import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseurl } from '../../baseurl';
import { 
  Trash2, FolderOpen, Calendar, 
  Layers, ChevronLeft, ChevronRight, Hash, 
  MapPin, ExternalLink, Info
} from 'lucide-react';

const AllBulk = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const adminToken = localStorage.getItem('adminToken');

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5); // Lowered slightly because each row is taller now

  useEffect(() => {
    fetchBulkHistory();
  }, [userId]);

  const fetchBulkHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseurl}getBulkUploadsByUser/${userId}`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      if (response.data.success) {
        setBatches(response.data.data);
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch bulk history' });
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleDeleteBulk = async (bulkId) => {
    const result = await Swal.fire({
      title: 'Delete Entire Batch?',
      text: "This will permanently remove the bulk record and all associated projects!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete all'
    });

    if (result.isConfirmed) {
      try {
        const res = await axios.delete(`${baseurl}deleteBulkUpload/${bulkId}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        if (res.data.success) {
          Swal.fire('Deleted!', 'The batch and projects have been removed.', 'success');
          fetchBulkHistory();
        }
      } catch (err) {
        Swal.fire('Error', 'Deletion failed.', 'error');
      }
    }
  };

  const filteredData = batches.filter(b => 
    b.bulk_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.projects?.some(p => p.property_id.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', { 
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', minHeight: '100vh' }}>
        <div className="container-fluid p-4">
          
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <h2 className="fw-bold mb-1" style={{ color: '#1B2559' }}>Bulk Management</h2>
              <p className="text-muted m-0">Detailed history and project breakdown for User ID: <b>{userId}</b></p>
            </div>
            <button onClick={() => navigate(-1)} className="btn btn-white shadow-sm border rounded-pill px-4 fw-bold">
               Back
            </button>
          </div>

          {/* SEARCH BOX */}
          <div className="card border-0 shadow-sm mb-4 p-3" style={{ borderRadius: '15px' }}>
            <div className="input-group border rounded-3 bg-white px-3 py-1">
              <span className="input-group-text bg-transparent border-0"><Hash size={18} className="text-muted"/></span>
              <input 
                type="text" 
                className="form-control border-0 shadow-none" 
                placeholder="Search by Batch Title or Property ID..." 
                onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
              />
            </div>
          </div>

          {/* MAIN DATA TABLE */}
          <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4 border-0 text-muted small fw-bold">BATCH INFO</th>
                    <th className="border-0 text-muted small fw-bold">PROJECT BREAKDOWN</th>
                    <th className="border-0 text-muted small fw-bold text-center">TOTAL</th>
                    <th className="border-0 text-muted small fw-bold">UPLOAD DETAILS</th>
                    <th className="border-0 text-muted small fw-bold text-end pe-4">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-5">Loading deep data...</td></tr>
                  ) : currentRows.length > 0 ? (
                    currentRows.map((batch, index) => (
                      <tr key={batch.id} style={{ borderBottom: '8px solid #F4F7FE' }}>
                        <td className="ps-4 align-top py-3">
                          <div className="d-flex align-items-start">
                            <div className="batch-icon-large me-3"><FolderOpen size={24} /></div>
                            <div>
                              <div className="fw-bold text-dark fs-5">{batch.bulk_title}</div>
                              <div className="badge bg-light text-primary border mt-1">ID: #BULK-{batch.id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3" style={{ minWidth: '400px' }}>
                          <div className="project-scroll-container">
                            {batch.projects && batch.projects.map((proj) => (
                              <div key={proj.id} className="project-detail-card mb-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="fw-bold text-primary" style={{fontSize: '12px'}}>{proj.property_id}</span>
                                  <button 
                                    className="btn-view-link"
                                    onClick={() => navigate(`/submission-details/${proj.id}`)}
                                  >
                                    <ExternalLink size={12} />
                                  </button>
                                </div>
                                <div className="text-muted d-flex align-items-center mt-1" style={{fontSize: '11px'}}>
                                  <MapPin size={10} className="me-1" /> {proj.address}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="text-center py-3">
                          <div className="h4 fw-bold text-dark mb-0">{batch.total_projects}</div>
                          <small className="text-muted">Projects</small>
                        </td>
                        <td className="py-3">
                          <div className="small text-dark fw-medium"><Calendar size={14} className="me-1"/> {formatDate(batch.created_at)}</div>
                          <div className="mt-2 small text-muted"><Info size={12} /> Auto-processed</div>
                        </td>
                        <td className="text-end pe-4 py-3">
                          <div className="d-flex flex-column gap-2 align-items-end">
                             <button 
                              className="btn btn-outline-danger btn-sm rounded-pill px-3"
                              onClick={() => handleDeleteBulk(batch.id)}
                            >
                              <Trash2 size={14} className="me-1"/> Delete Batch
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="5" className="text-center py-5 text-muted">No matching bulk data found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION FOOTER */}
            {!loading && totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top bg-white">
                <div className="text-muted small">Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, filteredData.length)} of {filteredData.length} batches</div>
                <div className="pagination-btns">
                  <button className="btn btn-sm btn-light border mx-1" disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}><ChevronLeft size={16}/></button>
                  <button className="btn btn-sm btn-primary mx-1 px-3">{currentPage}</button>
                  <button className="btn btn-sm btn-light border mx-1" disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)}><ChevronRight size={16}/></button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <style>{`
        .batch-icon-large { width: 50px; height: 50px; background: #E9EDF7; color: #4318FF; display: flex; align-items: center; justify-content: center; border-radius: 15px; }
        .project-scroll-container { max-height: 180px; overflow-y: auto; padding-right: 10px; }
        .project-scroll-container::-webkit-scrollbar { width: 4px; }
        .project-scroll-container::-webkit-scrollbar-thumb { background: #CBD5E0; border-radius: 10px; }
        
        .project-detail-card { background: #FFFFFF; border: 1px solid #E2E8F0; border-radius: 8px; padding: 10px; transition: all 0.2s; }
        .project-detail-card:hover { border-color: #4318FF; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
        
        .btn-view-link { background: none; border: none; color: #718096; transition: color 0.2s; }
        .btn-view-link:hover { color: #4318FF; }
        
        .btn-white { background: white; color: #1B2559; }
        .table thead th { font-size: 11px; text-transform: uppercase; letter-spacing: 0.8px; padding: 15px; background: #F8FAFC; }
      `}</style>
    </div>
  );
};

export default AllBulk;